import { useMemo } from "react";
import classnames from "classnames";
import { v4 as uuidv4 } from "uuid";
import useLanguage from "../../../core/i18n/useLanguage";
import inputStyles from "../Inputs/Input.module.css";
import { getWeekDays, getWeekDaysFull } from "../../utils/userDateFormat";

import styles from "./TagSelector.module.css";

export interface DaySelectorProps {
  label?: string;
  values: number[];
  setValues?: (selectedOptions: number[]) => void;
  validationError?: string;
  startFromSunday?: boolean;
  alignWithCron?: boolean;
}

export const DaySelector = ({
  label,
  values,
  setValues,
  validationError,
  startFromSunday = false,
  alignWithCron = false,
}: DaySelectorProps) => {
  const language = useLanguage();
  const locale = language.currentLanguage?.region || "sv-SE";
  const inputId = useMemo(() => uuidv4(), []);
  const errorId = useMemo(() => `${inputId}-error`, [inputId]);
  let weekdays = getWeekDays(locale);
  let weekdaysFull = getWeekDaysFull(locale);
  if (startFromSunday) {
    weekdays = [weekdays[6], ...weekdays.slice(0, 6)];
    weekdaysFull = [weekdaysFull[6], ...weekdaysFull.slice(0, 6)];
  }

  const options = useMemo(
    () =>
      weekdays.map((day, index) => ({
        id: alignWithCron ? index + 1 : index,
        title: day.slice(0, 2),
        fullName: weekdaysFull[index] ?? day,
        value: `day-${index}`,
      })),
    [weekdays, weekdaysFull, alignWithCron]
  );

  const selectedOptions = options.filter((o) => values?.includes(o.id));

  const clickHandler = (option: any) => {
    if (setValues) {
      const nextSelectedOptions = [...selectedOptions];
      const existingIndex = nextSelectedOptions.findIndex(
        (x) => x.value === option.value
      );
      if (existingIndex !== -1) {
        nextSelectedOptions.splice(existingIndex, 1);
      } else {
        nextSelectedOptions.push(option);
      }
      setValues(nextSelectedOptions.map((o) => o.id));
    }
  };

  return (
    <div className={styles.daySelectorWrapper}>
      {label && (
        <span id={inputId} className={inputStyles.label}>
          {label}
        </span>
      )}
      <div
        aria-labelledby={label ? inputId : undefined}
        aria-describedby={validationError ? errorId : undefined}
        className={classnames({
          [styles.tagSelector]: true,
          [styles.inputValidationError]: validationError !== undefined,
        })}
      >
        {options.map((option) => {
          const isSelected = selectedOptions.some(
            (x) => x.value === option.value
          );
          return (
            <button
              key={`option_${option.value}`}
              type="button"
              aria-pressed={isSelected}
              aria-label={option.fullName}
              className={classnames({
                [styles.tagOption]: true,
                [styles.dayOption]: true,
                [styles.dayOptionSelected]: isSelected,
              })}
              onClick={() => clickHandler(option)}
            >
              {option.title}
            </button>
          );
        })}
      </div>
      {validationError ? (
        <div id={errorId} role="alert" className={inputStyles.validationError}>
          {validationError}
        </div>
      ) : null}
    </div>
  );
};
export default DaySelector;
